/* Inline SVG icons. Replaces v1's ico.js global.

   A closed registry rather than arbitrary SVG input: every icon is reviewed
   once, sizes/strokes stay consistent, and an unknown name is a visible gap
   instead of a silent blank. */

import { LitElement, html, css, svg, nothing, type TemplateResult } from 'lit';
import { shadowReset } from '../../design/base.js';
import { customElement, property } from 'lit/decorators.js';

/** 24×24 viewBox, stroke-based so a single set works in both themes. */
const PATHS: Record<string, TemplateResult> = {
  library: svg`<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/>`,
  map: svg`<path d="M9 3.5 3.5 5.6v14.9L9 18.4l6 2.1 5.5-2.1V3.5L15 5.6z"/><path d="M9 3.5v14.9M15 5.6v14.9"/>`,
  index: svg`<path d="M5 4v16M13 4v16M3.5 9h17M3.5 15h17"/>`,
  /** Rows, for Map's view toggle. Distinct from `index`, which is a table. */
  list: svg`<path d="M4 6h2M4 12h2M4 18h2M9 6h11M9 12h11M9 18h11"/>`,
  settings: svg`<path d="M4 7h10M18 7h2M4 17h2M10 17h10"/><circle cx="16" cy="7" r="2.2"/><circle cx="8" cy="17" r="2.2"/>`,
  // A cog, distinct from `settings` above (sliders) — used where the shape
  // itself needs to read as "working"/mechanical, e.g. spinning in the
  // pull-to-refresh indicator.
  gear: svg`<circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>`,
  search: svg`<circle cx="10.5" cy="10.5" r="6.5"/><path d="m15.5 15.5 4 4"/>`,
  'collapse-left': svg`<path d="m13.5 8-4 4 4 4M18 8l-4 4 4 4"/>`,
  'collapse-right': svg`<path d="m10.5 8 4 4-4 4M6 8l4 4-4 4"/>`,
  'theme-auto': svg`<circle cx="12" cy="12" r="7"/><path d="M12 5v14" /><path d="M12 5a7 7 0 0 1 0 14z" fill="currentColor" stroke="none"/>`,
  'theme-light': svg`<circle cx="12" cy="12" r="4.2"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.8 5.8l1.4 1.4M16.8 16.8l1.4 1.4M18.2 5.8l-1.4 1.4M7.2 16.8l-1.4 1.4"/>`,
  'theme-dark': svg`<path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"/>`,
  import: svg`<path d="M12 4a8 8 0 1 1-7.6 5.5"/><path d="M4 4v6h6"/>`,
  edit: svg`<path d="M4 20h4l10-10a2.4 2.4 0 0 0-3.4-3.4L4.6 16.6z"/><path d="m13.5 7.5 3 3"/>`,
  trash: svg`<path d="M4.5 7h15M9.5 7V4.8h5V7M6.5 7l.9 12.2h9.2L17.5 7"/><path d="M10.5 11v5M13.5 11v5"/>`,
  plus: svg`<path d="M12 5v14M5 12h14"/>`,
  warn: svg`<path d="M12 4.5 21 19.5H3z"/><path d="M12 10v4.2M12 16.8v.6"/>`,
  copy: svg`<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M15 6.5V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h.5"/>`,
  check: svg`<path d="m5 12.5 4.5 4.5L19 7.5"/>`,
  eye: svg`<path d="M2.5 12S6 6 12 6s9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6z"/><circle cx="12" cy="12" r="2.8"/>`,
  'eye-off': svg`<path d="M4 4l16 16"/><path d="M9.5 6.4A9.6 9.6 0 0 1 12 6c6 0 9.5 6 9.5 6a17 17 0 0 1-2.6 3.2"/><path d="M6.6 8.1A17 17 0 0 0 2.5 12S6 18 12 18a9.4 9.4 0 0 0 3.3-.6"/>`,
  // Vertical three-dot overflow trigger. Drawn as filled dots rather than
  // stroked circles so it stays legible at the small icon size the row uses.
  'more-vertical': svg`<circle cx="12" cy="5" r="1.6" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.6" fill="currentColor" stroke="none"/><circle cx="12" cy="19" r="1.6" fill="currentColor" stroke="none"/>`,
  x: svg`<path d="M6 6l12 12M18 6L6 18"/>`,
  external: svg`<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>`,
  // Filled, unlike every other icon here: a play triangle drawn as an outline
  // reads as a hollow arrow at 13px, which is a different symbol.
  play: svg`<path d="M6 3.5 20 12 6 20.5z" fill="currentColor" stroke="none"/>`,
  // Filled for the same reason, and because it is play's twin in the same slot.
  pause: svg`<path d="M7 4h3.5v16H7zM13.5 4H17v16h-3.5z" fill="currentColor" stroke="none"/>`,
  back: svg`<path d="M19 12H5"/><path d="m11 6-6 6 6 6"/>`,
  'chev-up': svg`<path d="m6 14 6-6 6 6"/>`,
  'chev-down': svg`<path d="m6 10 6 6 6-6"/>`,
  // The cone is filled so the speaker still reads as solid at 18px over video,
  // where a hollow outline competes with whatever frame is behind it.
  vol: svg`<path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" fill="currentColor" stroke="none"/><path d="M15.5 9.5a4 4 0 0 1 0 5M18 7a7.5 7.5 0 0 1 0 10"/>`,
  mute: svg`<path d="M4 9.5h3.5L12 5.5v13L7.5 14.5H4z" fill="currentColor" stroke="none"/><path d="m16 10 4 4M20 10l-4 4"/>`,
  // The three-node network, not the box-and-up-arrow: `external` already spends
  // that shape on "open at the source", and Detail shows both on one screen.
  share: svg`<circle cx="18" cy="5" r="2.6"/><circle cx="6" cy="12" r="2.6"/><circle cx="18" cy="19" r="2.6"/><path d="m8.3 10.8 7.4-4.3M8.3 13.2l7.4 4.3"/>`,

  // Place kinds. Four of these are a categorical set — see KIND_META in
  // workspace/g-map/kinds.ts, which is what maps a server kind string onto one.
  utensils: svg`<path d="M4 3v7a2 2 0 0 0 4 0V3"/><path d="M6 12v9"/><path d="M18 3c-1.7 0-3 1.8-3 4v5h3"/><path d="M18 12v9"/>`,
  bed: svg`<path d="M2 20v-8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8"/><path d="M2 15h20"/><path d="M6 10V7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v3"/>`,
  camera: svg`<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z"/><circle cx="12" cy="13" r="3"/>`,
  bag: svg`<path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><path d="M3 6h18"/><path d="M16 10a4 4 0 0 1-8 0"/>`,
  pin: svg`<path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z"/><circle cx="12" cy="10" r="3"/>`,
  /** A venue with no coordinates — deliberately not a fifth kind icon. */
  ban: svg`<circle cx="12" cy="12" r="9"/><path d="M5.6 5.6l12.8 12.8"/>`,
};

@customElement('g-icon')
export class GIcon extends LitElement {
  static override styles = [shadowReset, css`
    :host {
      display: inline-flex;
      width: var(--icon-size, 20px);
      height: var(--icon-size, 20px);
      flex: none;
    }
    svg {
      width: 100%;
      height: 100%;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.6;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `];

  @property() name = '';

  override render() {
    const path = PATHS[this.name];
    if (!path) return nothing;
    return html`<svg viewBox="0 0 24 24" aria-hidden="true">${path}</svg>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-icon': GIcon;
  }
}
